import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { TrendingUp, Calendar, Users, DollarSign, ArrowRight, CheckCircle2 } from 'lucide-react';

const BRAND_NAME = "Cashflow";

export default function LandingPage() {
  const features = [
    {
      icon: Calendar,
      title: 'Recurring Expenses',
      description: 'Set up monthly, quarterly, 6-monthly, or annual expenses with automatic due date tracking.'
    },
    {
      icon: TrendingUp,
      title: '24-Month Forecast',
      description: 'See exactly what\'s due for the next 24 months with clear monthly totals.'
    },
    {
      icon: Users,
      title: 'Team Collaboration',
      description: 'Add team members to share cashflow visibility across your business.'
    },
    {
      icon: DollarSign,
      title: 'Payment Tracking',
      description: 'Mark payments as paid and toggle expenses on/off to adjust calculations.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="landing-page">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 md:px-12 py-6">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 bg-[#021d49] rounded-[4px] flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <span className="text-xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>{BRAND_NAME}</span>
        </div>
        <div className="flex items-center gap-4">
          <Link to="/login">
            <Button variant="ghost" className="text-stone-600 hover:text-[#021d49] rounded-[4px]" data-testid="nav-login-btn">
              Sign in
            </Button>
          </Link>
          <Link to="/register">
            <Button className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px]" data-testid="nav-register-btn">
              Get Started
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-0 min-h-[calc(100vh-88px)]">
        <div className="flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
          <div className="animate-slide-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#021d49] leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Know your cashflow.<br />
              <span className="text-stone-500">Plan your future.</span>
            </h1>
            <p className="text-lg text-stone-600 mt-6 max-w-lg leading-relaxed">
              Track recurring expenses, forecast 24 months ahead, and never miss a payment. 
              Built for business owners who need clarity, not complexity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <Link to="/register">
                <Button className="bg-[#021d49] hover:bg-[#032d6b] text-white rounded-[4px] h-12 px-8 text-base" data-testid="hero-cta-btn">
                  Start Free
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-stone-200 text-stone-600 hover:bg-stone-50 rounded-[4px] h-12 px-8 text-base">
                  Sign in
                </Button>
              </Link>
            </div>
            
            {/* Trust indicators */}
            <div className="flex items-center gap-6 mt-12 text-sm text-stone-500">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>AUD Currency</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Team Ready</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-600" />
                <span>Business & Personal</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="hidden md:block relative">
          <img
            src="https://images.pexels.com/photos/12732218/pexels-photo-12732218.jpeg"
            alt="Abstract geometric stone shapes"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#FAFAF9]/20" />
        </div>
      </section>

      {/* Features */}
      <section className="px-6 md:px-12 lg:px-24 py-24 bg-white">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Everything you need to manage cashflow
          </h2>
          <p className="text-stone-500 mt-4 max-w-2xl mx-auto">
            Simple, powerful tools to track expenses and forecast your financial future.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={feature.title}
              className="p-6 bg-stone-50 rounded-lg animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 bg-white rounded-[4px] flex items-center justify-center mb-4 shadow-sm">
                <feature.icon className="w-6 h-6 text-stone-600" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-[#021d49] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {feature.title}
              </h3>
              <p className="text-stone-500 text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24">
        <div className="bg-[#021d49] rounded-lg p-12 md:p-16 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to take control?
          </h2>
          <p className="text-blue-200 mb-8 max-w-xl mx-auto">
            Start tracking your expenses and forecasting your cashflow today.
          </p>
          <Link to="/register">
            <Button className="bg-white text-[#021d49] hover:bg-stone-100 rounded-[4px] h-12 px-8 text-base">
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="px-6 md:px-12 py-8 border-t border-stone-200">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#021d49] rounded-[4px] flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-bold text-[#021d49]" style={{ fontFamily: 'Manrope, sans-serif' }}>{BRAND_NAME}</span>
          </div>
          <p className="text-sm text-stone-500">
            © {new Date().getFullYear()} Solara. Cashflow made simple.
          </p>
        </div>
      </footer>
    </div>
  );
}
